import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Box, Stack } from "@mui/system";
import { Skeleton } from "@mui/material";
import MissingDataFilter from "./MissingDataFilter";
import MissingDataCardItem from "./MissingDataCardItem";
import {
  MISSING_DATA_SORT_OPTIONS,
  DEFAULT_MISSING_DATA_SORT,
  sortAnomaliesRows,
} from "../Common/anomaliesSort";
import { useBizDevelopmentDashboardMobile } from "../../../../../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboardMobile";
import { exportRowsToXlsx } from "../../../../../../DashboardDesktop/Anomalies/AnomaliesTabs/Utils/anomaliesColumns";
import { MISSING_DATA_EXPORT_COLUMNS } from "../../../../../../DashboardDesktop/Anomalies/AnomaliesTabs/AnomaliesMissingData/AnomaliesMissingDataTable";

const MissingDataSkeleton = () => (
  <Box sx={{ mt: 2 }}>
    <Stack spacing={2}>
      {[1, 2, 3].map((item) => (
        <Box
          key={item}
          sx={{ borderRadius: 2, border: "1px solid #e0e0e0", p: 2 }}
        >
          <Stack spacing={1.2}>
            <Skeleton width={140} height={20} />
            <Stack direction="row" justifyContent="space-between">
              <Skeleton width={160} height={18} />
              <Skeleton width={80} height={24} />
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Skeleton width={120} height={18} />
              <Skeleton width={120} height={18} />
            </Stack>
          </Stack>
        </Box>
      ))}
    </Stack>
  </Box>
);

const MissingData = () => {
  const [searchParams] = useSearchParams();
  const workspaceParam = searchParams.get("workspace");

  const isLoading = useSelector(
    (state: any) => state.bizDevelopmentAnomalies.loaderMissingData,
  );
  const output = useSelector(
    (state: any) => state.bizDevelopmentAnomalies.missingData,
  );

  const { fetchMissingData, getSiteFromWorkspace } =
    useBizDevelopmentDashboardMobile();

  const [brandName, setBrandName] = useState("");
  const [sortKey, setSortKey] = useState(DEFAULT_MISSING_DATA_SORT.key);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(
    DEFAULT_MISSING_DATA_SORT.dir,
  );

  useEffect(() => {
    const sites = getSiteFromWorkspace();
    if (sites.length === 0) return;
    fetchMissingData(sites);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceParam]);

  const brandList = useMemo(() => {
    const set = new Set<string>();
    (output ?? []).forEach((row: Record<string, any>) => {
      const name = String(row?.brand_display_name ?? "").trim();
      if (name) set.add(name);
    });
    return Array.from(set).sort();
  }, [output]);

  const filteredOutput = useMemo(() => {
    const search = brandName.trim().toLowerCase();
    if (!search) return output ?? [];
    return (output ?? []).filter((row: Record<string, any>) =>
      String(row?.brand_display_name ?? "")
        .toLowerCase()
        .includes(search),
    );
  }, [output, brandName]);

  const sortedOutput = useMemo(
    () => sortAnomaliesRows(filteredOutput, sortKey, sortDir),
    [filteredOutput, sortKey, sortDir],
  );

  const brandCount = filteredOutput.length;

  const handleApplySort = (key: string, dir: "asc" | "desc") => {
    setSortKey(key);
    setSortDir(dir);
  };

  const handleDownload = () => {
    exportRowsToXlsx(
      sortedOutput,
      MISSING_DATA_EXPORT_COLUMNS,
      "missing-data.xlsx",
    );
  };

  return (
    <Box sx={{ px: 2 }}>
      <MissingDataFilter
        brandCount={brandCount}
        onDownload={handleDownload}
        downloadDisabled={isLoading || brandCount === 0}
        sortKey={sortKey}
        sortDir={sortDir}
        onApplySort={handleApplySort}
        sortOptions={MISSING_DATA_SORT_OPTIONS}
        brandList={brandList}
        brandName={brandName}
        onBrandNameChange={setBrandName}
      />
      {isLoading ? (
        <MissingDataSkeleton />
      ) : (
        <MissingDataCardItem sortedData={sortedOutput} />
      )}
    </Box>
  );
};

export default MissingData;
